/* eslint-disable react-hooks/exhaustive-deps */
import {Screen} from "components/ui"
import {useTranslation} from "react-i18next"
import {useLocation} from "react-router-dom"
import {BaseDepartmentId} from "types/departments"
import {useEffect, useMemo, useReducer, useState} from "react"
import {handleError, toastError, toastSuccess, getEnableSubunitsByDepartmentPermission, getFullName} from "helpers"
import {useModel, useAllDepartmentSubunits, useStaffInfo} from "hooks"
import {BaseButton, BaseLoading} from "components"
import styles from "./GraduationReportR41.module.css"
import {Auth} from "types/auth"
import {Col, Row} from "antd"
import UserPaginationSelect from "components/UserPaginationSelect"
import {UserAutocomplete} from "types/user"
import {Iframe} from "components/Iframe"
import {useDocumentActions} from "hooks"
import {Alert} from "antd"
import {PermissionType} from "types/permission"
import {KlassDropdown} from "components/Select"
import {userServiceV3} from "services"

function reducer(state, action) {
    return {...state, ...action}
}

type LocationState = {
    backUrl: string
}

const GraduationReportR41 = () => {
    const {t} = useTranslation(["reports", "common"])
    const location = useLocation()
    const model = useModel()
    const {backUrl} = (location.state as LocationState) || {backUrl: "/reports?tab=academics"}
    const {bulkCreate, isBulkCreating} = useDocumentActions()
    const [pdfFile, setPdfFile] = useState<File | null>(null)
    const [error, setError] = useState<string | null>(null)
    const {subunits} = useAllDepartmentSubunits()
    const {academicStages} = useStaffInfo()

    const initialState = {
        isLoadingIframe: false,
        baseWebFormsUrl: `${process.env.REACT_APP_WEBFORMS_BASE_URL}/graduation-report-r41/?token=${model.authToken}&profileId=${model.profileId}`,
        currentProfile: null,
        reportUrl: null,
        instructors: [],
        academicStageIds: [],
        instructorOptions: []
    }
    const [
        {isLoadingIframe, baseWebFormsUrl, currentProfile, reportUrl, instructors, academicStageIds, instructorOptions},
        dispatch
    ] = useReducer(reducer, initialState)

    const academicsSubunit = useMemo(() => {
        return subunits.find((subunit) => subunit.departmentId === BaseDepartmentId.Academics)
    }, [subunits])

    useEffect(() => {
        if (academicStages.length) {
            dispatch({academicStageIds: academicStages.map((stage) => stage.statusId)})
        } else {
            dispatch({academicStageIds: []})
        }
    }, [academicStages])

    useEffect(() => {
        getInstructorOptions()
    }, [currentProfile])

    const getInstructorOptions = async () => {
        if (currentProfile) {
            const campuses = currentProfile ? [currentProfile.campusId] : []
            const {data} = await userServiceV3.getAll({
                range: {
                    limit: 100,
                    offset: 0
                },
                filter: {
                    type: [Auth.UserProfileType.Staff],
                    ...(currentProfile?.campusId ? {campus: campuses} : {}),
                    isInstructor: true
                },
                linkedObjects: true
            })
            const staffs = data
                .map((staff) => ({
                    id: staff.id,
                    fullName: getFullName(staff)
                }))
                .filter((staff) => staff.id)
            dispatch({
                instructorOptions: staffs,
                instructors: staffs
            })
        } else {
            dispatch({instructors: []})
        }
    }

    const handleGenerate = async () => {
        if (!currentProfile) {
            toastError(t("studentAccount.studentTransactions.studentRequired"))
            return
        }

        if (!instructors || instructors.length === 0) {
            toastError(t("graduationReportR41.instructorRequired"))
            return
        }

        setError(null)
        setPdfFile(null)
        dispatch({isLoadingIframe: true, reportUrl: null})

        try {
            const instructorIds = instructors.map((instructor) => instructor.id)
            const reportUrl = `${baseWebFormsUrl}&studentProfileId=${
                currentProfile.profileId
            }&pdf&instructorIds=${instructorIds.join(",")}`
            const response = await fetch(reportUrl)

            if (!response.ok) {
                const errorText = await response.text()
                throw new Error(errorText || `Error: ${response.status} ${response.statusText}`)
            }

            const blob = await response.blob()

            if (!blob.type.includes("pdf")) {
                throw new Error("The received file is not a valid PDF")
            }

            const fileName = `graduation-report-r41-${currentProfile.profileId}-${Date.now()}.pdf`
            const file = new File([blob], fileName, {type: "application/pdf"})
            setPdfFile(file)

            const blobUrl = URL.createObjectURL(blob)
            dispatch({reportUrl: blobUrl})

            toastSuccess("PDF generated successfully")
        } catch (e) {
            const err: any = e
            console.error("Error generating PDF:", err)
            setError(err.message || "Error generating PDF")
            toastError("Error generating PDF")
        } finally {
            dispatch({isLoadingIframe: false})
        }
    }

    const onLoad = () => {
        dispatch({isLoadingIframe: false})
    }

    const handleSaveDocument = async () => {
        if (!pdfFile) {
            toastError("No PDF available to save")
            return
        }

        if (!currentProfile) {
            toastError("Please select a student")
            return
        }

        if (!academicsSubunit) {
            toastError("No accessible Academics subunit found")
            return
        }

        try {
            const formData = new FormData()
            const fileExtension = pdfFile.name.split(".").pop()
            const documentId = Date.now().toString()

            const payload = {
                ownerUserProfileId: currentProfile.profileId,
                ownerUserId: currentProfile.id,
                departmentSubunitId: academicsSubunit.subunitId,
                isVisibleForUser: true,
                isOriginal: true,
                name: `Graduation Report R41 - ${currentProfile.fullName}`,
                description: "Graduation Report R41",
                id: documentId,
                departmentId: BaseDepartmentId.Academics,
                studentChecklistItemIds: [],
                studentActivityIds: [],
                notifyStudent: false
            }

            formData.append("document", pdfFile, `${documentId}.${fileExtension}`)
            formData.append("data", JSON.stringify(payload))

            bulkCreate(formData, {
                onSuccess: () => {
                    toastSuccess("Document saved successfully")

                    // Download PDF after successful save
                    const blobUrl = URL.createObjectURL(pdfFile)
                    const link = document.createElement("a")
                    link.href = blobUrl
                    link.download = pdfFile.name
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(blobUrl)
                }
            })
        } catch (err) {
            handleError(err)
        }
    }
    return (
        <Screen
            htmlTitle={t("graduationReportR41.title")}
            header={{
                title: t("graduationReportR41.title"),
                backUrl
            }}>
            <Row gutter={[16, 24]} align="middle" className={styles.filter}>
                <Col span={6}>
                    <UserPaginationSelect
                        placeholder={t("graduationReportR41.student")}
                        isMulti={false}
                        type={Auth.UserProfileType.Student}
                        value={currentProfile}
                        onChange={(option: UserAutocomplete) => {
                            dispatch({currentProfile: option})
                            setError(null)
                            setPdfFile(null)
                            dispatch({reportUrl: null})
                        }}
                        campusIds={[2, 4, 6]}
                        studentStatusIds={academicStageIds}
                    />
                </Col>
                {currentProfile && (
                    <Col span={10}>
                        <KlassDropdown
                            isClearable
                            options={instructorOptions}
                            valueKey="id"
                            labelKey="fullName"
                            value={instructors}
                            onChange={(newValue) => dispatch({instructors: newValue})}
                            className={styles.contentInput}
                            menuPortalTarget={document.body}
                            isMulti
                        />
                    </Col>
                )}
                <Col span={8}>
                    <div className={styles.actionWrap}>
                        <BaseButton
                            title={t("graduationReportR41.generate")}
                            onClick={handleGenerate}
                            loading={isLoadingIframe}
                        />
                        <BaseButton
                            title={t("graduationReportR41.downloadAndSave")}
                            onClick={handleSaveDocument}
                            disabled={!pdfFile}
                            loading={isBulkCreating}
                        />
                    </div>
                </Col>
            </Row>

            {error && (
                <Row style={{marginTop: "20px"}}>
                    <Col span={24}>
                        <Alert
                            message={t("graduationReportR41.errorGeneratingPdf")}
                            description={error}
                            type="error"
                            closable
                            onClose={() => setError(null)}
                            showIcon
                        />
                    </Col>
                </Row>
            )}

            {reportUrl && !error && <Iframe id="web_forms" iframe_url={reportUrl} title="Report" onLoad={onLoad} />}

            <BaseLoading isShow={isLoadingIframe || isBulkCreating} />
        </Screen>
    )
}

export default GraduationReportR41
